import React, { useMemo } from 'react';
import { GivelifyInfiniteScroll } from '@givelify/ui';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { ErrorText } from 'components/ErrorText';
import { useAdvancedTranslation } from 'utils/i18n';
import { EnvelopeListItem } from '../components/EnvelopeListItem';
import {
    EnvelopesContainer,
    EnvelopesListContainer,
} from '../components/envelopesPageStyle';
import NoItemsPanel from '../components/NoItemsPanel';
import { useEnvelopesContext } from '../context/EnvelopesProvider';

const InactiveEnvelopesPage: React.FCC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));
    const { at, t } = useAdvancedTranslation();
    const copy = useMemo(
        () => ({
            title: at('pages.settings.envelopes2.inactive-envelopes-tab.title'),
            pageLoadFail: t('labels.page-load-fail'),
            zero: at('pages.settings.envelopes2.inactive-envelopes-tab.zero'),
        }),
        [t, at],
    );

    const {
        inactiveEnvelopes: data,
        getInactiveEnvelopesRequestState: requestState,
        hasFullAccess,
        editEnvelopeClick,
        updatePrioritiesRequestState,
        movedItemId,
        rollbackDragAndDrop,
        loadNewInactivePage: loadNextPage,
    } = useEnvelopesContext();

    const isEmpty =
        !data || data?.data?.length === 0 || data?.pagination?.total === 0;

    const hasMore =
        (data?.pagination?.currentPage || 0) <
        (data?.pagination?.totalPages || 0);

    return (
        <EnvelopesContainer>
            <EnvelopesListContainer aria-label="Inactive envelopes list">
                <GivelifyInfiniteScroll
                    empty={isEmpty}
                    error={requestState === 'REQUEST_ERROR'}
                    errorComponent={<ErrorText text={copy.pageLoadFail} />}
                    hasMore={hasMore}
                    loadMore={loadNextPage}
                    loading={
                        requestState === 'REQUEST_START' &&
                        (!data?.data || data.data.length === 0)
                    }
                    loadingComponent={<div />}
                    zerothComponent={<NoItemsPanel type="hidden" />}
                >
                    <Grid
                        container
                        flexDirection="column"
                        flexWrap="nowrap"
                        gap={isMobile ? '16px' : '24px'}
                    >
                        {data.data.map((item) => (
                            <EnvelopeListItem
                                key={item.id}
                                editEnvelopeClick={editEnvelopeClick}
                                hasFullAccess={hasFullAccess}
                                movedItemId={movedItemId}
                                rollbackDragAndDrop={rollbackDragAndDrop}
                                updatePrioritiesRequestState={
                                    updatePrioritiesRequestState
                                }
                                {...item}
                            />
                        ))}
                    </Grid>
                </GivelifyInfiniteScroll>
            </EnvelopesListContainer>
        </EnvelopesContainer>
    );
};

export default InactiveEnvelopesPage;
